// this hook handles all the state and logic for the add session modal

import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { handleCreateNewSession } from './handle-create-new-session';
import useGetWorkingProjectThemeColorsList from '@/utils/colors/use-get-working-project-theme-colors-list';
import { Speaker } from '@/types/speakers.types';
import { getDefaultFeaturesByProjectType } from '../../session-features/session-features-helpers';
import { BreakoutRoom, EBroadcastTypes } from '@/types/session.types';
import { ESessionFeatures } from '@/types/session-features.types';
import { IEventTag } from '@/types/projects.types';
import { getDefaultLanguage } from '@/utils/get-default-language';
import useCanUseChannelFeature from '@/utils/use-can-use-channel-features';
import { ChannelFeaturesEnum } from '@/types/feature-flags.types';
import checkIfProjectCreatedFromTemplate from '@/utils/checkIfProjectCreatedFromTemplate';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getActiveChannel, getUser } from '@/store/slices/user/selectors';
import { getWorkingProject, selectIsCreatingNewSession } from '@/store/slices/project/selectors';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { TBroadcastTypePickerOptions } from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/broadcast-type-picker/broadcast-type-picker.types';
import getDefaultBreakoutRoom from './helpers/get-default-breakout-room';
import { DateRange } from '@ui/calendar/types';
import { getUserTimezone } from '@ui/calendar/utils';
import { setIsCreatingNewSession } from '@/store/slices/project';
import TranslateLangs from '@/utils/translate-langs.json';

type TUseAddSessionModalProps = {
  handleCloseModal?: () => void;
  navigateTo?: string;
};

// TODO: narrow this to Brazilian Portuguese when we implement:
// https://brandlive.atlassian.net/browse/BDEV-5460
const ALLOWED_AUTO_CAPTIONS_LANGUAGES = new Set(['en', 'fr', 'de', 'it', 'es', 'pt']);

const useAddSessionModal = (props?: TUseAddSessionModalProps) => {
  const { handleCloseModal } = props || {};
  const project = useAppSelector(getWorkingProject);
  const user = useAppSelector(getUser);
  const channel = useAppSelector(getActiveChannel);
  const isCreatingNewSession = useAppSelector(selectIsCreatingNewSession);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const paramsLanguage = params?.language || 'en';

  const dispatch = useAppDispatch();

  const isProjectCreatedFromTemplate = checkIfProjectCreatedFromTemplate(project?.settings);

  const isCaptionsChannelFeatureOn = useCanUseChannelFeature(ChannelFeaturesEnum.mux_auto_transcripts);
  const defaultProjectLanguage = getDefaultLanguage(project);

  const [defaultLanguage, setDefaultLanguage] = useState<string>(defaultProjectLanguage);
  const [hostEmails, setHostEmails] = useState<TReactSelectOption<string, string>[]>([]);
  const canEnableAutoCaptions = isCaptionsChannelFeatureOn && ALLOWED_AUTO_CAPTIONS_LANGUAGES.has(defaultLanguage);
  const defaultLanguageOption = useMemo(
    () => ({
      label: defaultLanguage as string,
      value: defaultLanguage as string,
    }),
    [defaultLanguage]
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedBroadcastType, setSelectedBroadcastType] = useState<TBroadcastTypePickerOptions>(
    EBroadcastTypes.greenroom
  );
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [times, setTimes] = useState<{ timestamp: number | null; end_timestamp: number | null }>({
    timestamp: null,
    end_timestamp: null,
  });
  const [requiresTimeChange, setRequiresTimeChange] = useState({
    start_date: true,
    start_time: true,
    end_date: true,
    end_time: true,
  });
  const [selectedTimezone, setSelectedTimezone] = useState<string>(getUserTimezone());
  const [speakers, setSpeakers] = useState<Speaker[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [tags, setTags] = useState<IEventTag[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<TReactSelectOption[]>([defaultLanguageOption]);
  const [selectedFeatures, setSelectedFeatures] = useState<ESessionFeatures[]>(() => {
    if (!isProjectCreatedFromTemplate) {
      return [];
    }
    const disabledFeatures = new Set<ESessionFeatures>();
    if (!canEnableAutoCaptions) {
      disabledFeatures.add(ESessionFeatures.CAPTIONS);
    }
    return getDefaultFeaturesByProjectType(project?.event_type, { disabledFeatures }).map((f) => f.value);
  });
  const [breakoutRooms, setBreakoutRooms] = useState<BreakoutRoom[]>([getDefaultBreakoutRoom()]);

  const projectType = project?.event_type;

  const resetState = useCallback(() => {
    setName('');
    setDescription('');
    setSelectedBroadcastType(EBroadcastTypes.greenroom);
    setImageUrl(null);
    setTimes({ timestamp: null, end_timestamp: null });
    setRequiresTimeChange({
      start_date: true,
      start_time: true,
      end_date: true,
      end_time: true,
    });
    setSpeakers([]);
    setTags([]);
    setCategories([]);
    setSelectedFeatures(() => {
      if (!isProjectCreatedFromTemplate) {
        return [];
      }
      return getDefaultFeaturesByProjectType(project?.event_type).map((f) => f.value);
    });
    setSelectedLanguages([defaultLanguageOption]);
    setDefaultLanguage(defaultProjectLanguage);
    setHostEmails([]);
    setBreakoutRooms([getDefaultBreakoutRoom()]);
  }, [defaultLanguageOption, defaultProjectLanguage, isProjectCreatedFromTemplate, project?.event_type]);

  const handleNameChange = useCallback(
    (value: string) => {
      if (isCreatingNewSession) return;
      setName(value);
    },
    [isCreatingNewSession]
  );

  const handleDescriptionBlur = useCallback(
    (value: string) => {
      if (isCreatingNewSession) return;
      setDescription(value);
    },
    [isCreatingNewSession]
  );

  const onImageUpload = async (url: string) => setImageUrl(url);

  const handleClearImage = () => setImageUrl(null);

  const handleDateChange = ({ startDate, endDate, selectedTimezone }: DateRange) => {
    setTimes({
      timestamp: startDate,
      end_timestamp: endDate,
    });
    setRequiresTimeChange({
      start_date: false,
      start_time: false,
      end_date: false,
      end_time: false,
    });
    setSelectedTimezone(selectedTimezone);
  };

  const handleAddSpeakers = useCallback((selectedSpeakers: { label: string; value: number; meta?: Speaker }[]) => {
    const updatedSpeakers = selectedSpeakers.map((s) => s.meta).filter((meta): meta is Speaker => Boolean(meta));
    setSpeakers(updatedSpeakers);
  }, []);

  const handleAddCategories = (selectedCategories: TReactSelectOption[]) => {
    const updatedCategories = selectedCategories.map((c) => c.value.toString());
    setCategories(updatedCategories);
  };

  const presetColors = useGetWorkingProjectThemeColorsList();

  const displayTags = useMemo(() => {
    // remove dupes (needed because saving adds the working tags into the project tags)
    // and we briefly have duplicates
    const tagsMap: Record<string, string> = {};
    return tags.filter((tag) => {
      if (tagsMap[tag.uuid]) return false;
      tagsMap[tag.uuid] = tag.uuid;
      return true;
    });
  }, [tags]);

  const resetToSingleLanguage = useCallback(() => {
    // start with English
    let singleLanguage: TReactSelectOption<string, string, Record<string, string>> = {
      label: 'English',
      value: 'en',
      meta: { label: 'English', value: 'en' },
    };
    // if we have a selected language, overwrite English
    if (selectedLanguages?.[0]) {
      singleLanguage = {
        label: selectedLanguages[0].label.toString(),
        value: selectedLanguages[0].value.toString(),
        meta: {
          label: selectedLanguages[0].label.toString(),
          value: selectedLanguages[0].value.toString(),
        },
      };
    }
    // if we have a default language, overwrite the selected language
    if (defaultLanguage) {
      const matchedLabel = TranslateLangs.find((lang) => lang.code === defaultLanguage)?.label || defaultLanguage;
      singleLanguage = {
        label: matchedLabel,
        value: defaultLanguage,
        meta: { label: matchedLabel, value: defaultLanguage },
      };
    }

    setSelectedLanguages([singleLanguage]);
    setDefaultLanguage(singleLanguage.value);
  }, [defaultLanguage, selectedLanguages]);

  const checkIfSessionNameExists = useCallback(() => {
    if (isCreatingNewSession) return false;

    const sessionNameExists = project?.sessions.find((s) => {
      return s.title.base.trim().toLowerCase() === name.trim().toLowerCase();
    });

    return !!sessionNameExists;
  }, [isCreatingNewSession, name, project?.sessions]);

  const isValid = useCallback(() => {
    if (!name.trim().length) return false;

    const sessionNameExists = checkIfSessionNameExists();
    if (sessionNameExists) return false;

    if (!times.timestamp) return false;
    if (!times.end_timestamp) return false;
    if (times.timestamp >= times.end_timestamp) return false;

    return true;
  }, [checkIfSessionNameExists, name, times.end_timestamp, times.timestamp]);

  const onSave = useCallback(async () => {
    dispatch(setIsCreatingNewSession(true));
    try {
      await handleCreateNewSession({
        project,
        user,
        channel,
        dispatch,
        userSelections: {
          name,
          description,
          times,
          imageUrl,
          selectedBroadcastType,
          categories,
          tags: displayTags,
          speakers,
          features: selectedFeatures,
          languages: selectedLanguages.map((lang) => lang.value.toString()),
          defaultLanguage,
          firesidesHosts: hostEmails.map((email) => email.value),
          breakoutRooms,
        },
        navigate,
        location,
        paramsLanguage,
        handleCloseModal,
        navigateTo: props?.navigateTo,
      });
    } catch (e) {
      console.error('Error creating new session:', e);
    } finally {
      dispatch(setIsCreatingNewSession(false));
    }
  }, [
    project,
    user,
    channel,
    dispatch,
    name,
    description,
    times,
    imageUrl,
    selectedBroadcastType,
    categories,
    displayTags,
    speakers,
    selectedFeatures,
    selectedLanguages,
    defaultLanguage,
    navigate,
    location,
    paramsLanguage,
    hostEmails,
    breakoutRooms,
    handleCloseModal,
    props?.navigateTo,
  ]);

  return useMemo(() => {
    return {
      resetState,
      onImageUpload,
      handleClearImage,
      handleNameChange,
      handleDateChange,
      handleDescriptionBlur,
      handleAddSpeakers,
      handleAddCategories,
      onSave,
      checkIfSessionNameExists,
      isValid,
      savingSession: isCreatingNewSession,
      projectType,
      imageUrl,
      name,
      times,
      requiresTimeChange,
      description,
      selectedBroadcastType,
      selectedFeatures,
      speakers,
      displayTags,
      presetColors,
      setSelectedBroadcastType,
      setSelectedFeatures,
      setTags,
      setSelectedLanguages,
      selectedLanguages,
      defaultLanguage,
      setDefaultLanguage,
      hostEmails,
      setHostEmails,
      breakoutRooms,
      setBreakoutRooms,
      selectedTimezone,
      resetToSingleLanguage,
    };
  }, [
    checkIfSessionNameExists,
    description,
    displayTags,
    handleAddSpeakers,
    handleDescriptionBlur,
    handleNameChange,
    imageUrl,
    isValid,
    name,
    onSave,
    presetColors,
    projectType,
    requiresTimeChange,
    resetState,
    isCreatingNewSession,
    selectedBroadcastType,
    selectedFeatures,
    speakers,
    times,
    selectedLanguages,
    defaultLanguage,
    hostEmails,
    setHostEmails,
    breakoutRooms,
    setBreakoutRooms,
    selectedTimezone,
    resetToSingleLanguage,
  ]);
};

export default useAddSessionModal;
